import React, { useRef, useState } from 'react';
import {
  FaFileUpload,
  FaFileCsv,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaInfoCircle,
  FaCloudUploadAlt,
  FaChevronDown,
} from 'react-icons/fa';
import { previewImport, commitAndExecuteImport } from '../services/batchImportService';
import { formatDate } from '../utils/dateUtils';
import ConfirmationModal from './ConfirmationModal';
import SyncResultsModal from './SyncResultsModal';

// Dev-only testing aid: mock mode commits drafts and emails RCTIs but never calls Xero, so a live
// Xero org is not written to. The checkbox only renders in the dev environment.
const IS_DEV_ENV = process.env.REACT_APP_FIREBASE_PROJECT_ID === 'loope-dev-env';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatCurrency = (val) => {
  if (val === null || val === undefined || Number.isNaN(Number(val))) return 'N/A';
  return `$${Number(val).toFixed(2)}`;
};

// Preview dates come back as Firestore-shaped timestamps ({_seconds}) but may
// also arrive as plain ISO strings depending on the serializer, so try both.
const formatPreviewDate = (value) => {
  if (!value) return 'N/A';
  if (value._seconds) return formatDate(value);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const getStatusPillProps = (status) => {
  const normalized = status || '';
  const pillClass = normalized === 'deleted' || normalized === 'void'
    ? 'bg-red-100 text-red-800'
    : normalized === 'awaiting_payment'
    ? 'bg-orange-100 text-orange-600'
    : normalized === 'paid'
    ? 'bg-green-100 text-green-800'
    : normalized === 'draft'
    ? 'bg-yellow-100 text-yellow-800'
    : 'bg-gray-100 text-gray-800';

  const label = normalized
    ? normalized.charAt(0).toUpperCase() + normalized.slice(1).replace('_', ' ')
    : 'N/A';

  return { pillClass, label };
};

// The distinguishing pill for a row that WILL be imported (tagged imported:true on commit).
// A skipped row never becomes a doc, so it must not also claim to be imported.
const ImportedPill = () => (
  <span className="px-2 py-1 inline-flex text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
    Imported
  </span>
);

const OkPill = ({ ok }) => (
  ok ? (
    <span className="px-2 py-1 inline-flex items-center gap-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
      <FaCheckCircle /> OK
    </span>
  ) : (
    <span className="px-2 py-1 inline-flex items-center gap-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
      <FaTimesCircle /> Skipped
    </span>
  )
);

const SummaryCard = ({ icon: Icon, label, value, colorClass }) => (
  <div className={`bg-white rounded-lg border p-4 flex items-center gap-4 shadow-sm ${colorClass}`}>
    <Icon className="text-3xl" />
    <div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  </div>
);

// ─── Sub-tables ─────────────────────────────────────────────────────────────

// Chevron toggle shared by both preview tables. Rotation only (no layout animation).
const ExpandToggle = ({ isOpen, onToggle, docNumber }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={isOpen}
    aria-label={`${isOpen ? 'Hide' : 'Show'} breakdown for ${docNumber}`}
    className="p-1.5 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-jims-cyan"
  >
    <FaChevronDown className={`transition-transform duration-150 ${isOpen ? '' : '-rotate-90'}`} />
  </button>
);

const InvoicesPreviewTable = ({ invoices }) => {
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (!invoices || invoices.length === 0) {
    return (
      <div className="p-6 text-center text-gray-500 bg-white rounded-lg border border-gray-200">
        No invoices found in this batch.
      </div>
    );
  }

  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-x-auto">
      <table className="min-w-full leading-normal">
        <thead>
          <tr className="bg-gray-200 text-left text-gray-600 uppercase text-sm">
            <th className="pl-3 py-3 border-b-2 border-gray-200 w-10"><span className="sr-only">Breakdown</span></th>
            <th className="px-5 py-3 border-b-2 border-gray-200">Status</th>
            <th className="px-5 py-3 border-b-2 border-gray-200">Invoice #</th>
            <th className="px-5 py-3 border-b-2 border-gray-200">Supplier</th>
            <th className="px-5 py-3 border-b-2 border-gray-200">Franchisee</th>
            <th className="px-5 py-3 border-b-2 border-gray-200">Line Items</th>
            <th className="px-5 py-3 border-b-2 border-gray-200">Amount</th>
            <th className="px-5 py-3 border-b-2 border-gray-200">Xero Contact</th>
            <th className="px-5 py-3 border-b-2 border-gray-200">Loope Status</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv) => {
            const { pillClass, label } = getStatusPillProps(inv.status || 'draft');
            const isOpen = expanded.has(inv.externalDocNumber);
            const lineItems = inv.lineItems || [];
            return (
              <React.Fragment key={inv.externalDocNumber}>
                <tr className="hover:bg-gray-50">
                  <td className="pl-3 py-4 border-b border-gray-200">
                    <ExpandToggle isOpen={isOpen} onToggle={() => toggle(inv.externalDocNumber)} docNumber={inv.externalDocNumber} />
                  </td>
                  <td className="px-5 py-4 border-b border-gray-200 text-sm"><OkPill ok={inv.ok !== false} /></td>
                  {/* The invoice number and the reference are two different things now: the FM number goes
                      to Xero as the invoice number, the job number underneath it as the reference. */}
                  <td className="px-5 py-4 border-b border-gray-200 text-sm font-medium text-gray-900">
                    <div>{inv.externalDocNumber}</div>
                    {inv.jobReference && <div className="text-xs font-normal text-gray-400">Ref {inv.jobReference}</div>}
                  </td>
                  <td className="px-5 py-4 border-b border-gray-200 text-sm text-gray-700">{inv.contactName || inv.ContactName || 'N/A'}</td>
                  <td className="px-5 py-4 border-b border-gray-200 text-sm text-gray-700">{inv.fseCode || 'N/A'}</td>
                  <td className="px-5 py-4 border-b border-gray-200 text-sm text-gray-700">{lineItems.length || 1}</td>
                  <td className="px-5 py-4 border-b border-gray-200 text-sm text-gray-700">
                    {formatCurrency(inv.unitAmount)}
                  </td>
                  <td className="px-5 py-4 border-b border-gray-200 text-sm text-gray-700">
                    {inv.xeroContactId ? (
                      <span className="inline-flex items-center gap-1 text-green-700"><FaCheckCircle /> Resolved</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-600"><FaTimesCircle /> Not found</span>
                    )}
                  </td>
                  <td className="px-5 py-4 border-b border-gray-200 text-sm">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${pillClass}`}>{label}</span>
                      {inv.ok !== false && <ImportedPill />}
                    </div>
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={9} className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                      <p className="text-xs text-gray-500 mb-3">
                        From <span className="font-medium text-gray-700">{inv.sourceFile || 'the uploaded Xero invoice file'}</span>
                        {', amounts are ex GST; Xero adds GST per line tax type.'}
                      </p>
                      <p className="text-xs text-gray-500 mb-3">
                        Goes to Xero as invoice <span className="font-medium text-gray-700">{inv.externalDocNumber}</span>
                        {inv.jobReference
                          ? <> with reference <span className="font-medium text-gray-700">{inv.jobReference}</span>.</>
                          : <> (the file gave no job number to use as a reference).</>}
                      </p>
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-xs uppercase text-gray-500 text-left">
                            <th className="py-1 pr-4 font-medium">CSV row</th>
                            <th className="py-1 pr-4 font-medium">Description</th>
                            <th className="py-1 pr-4 font-medium">Item code</th>
                            <th className="py-1 pr-4 font-medium text-right">Qty</th>
                            <th className="py-1 pr-4 font-medium text-right">Unit (ex GST)</th>
                            <th className="py-1 pr-4 font-medium text-right">Line total</th>
                            <th className="py-1 pr-4 font-medium">Account</th>
                            <th className="py-1 font-medium">Tax type</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {lineItems.map((li, i) => (
                            <tr key={li.rowNumber ?? i}>
                              <td className="py-1.5 pr-4 text-gray-500">{li.rowNumber ?? 'N/A'}</td>
                              <td className="py-1.5 pr-4 text-gray-700">{li.description || 'N/A'}</td>
                              <td className="py-1.5 pr-4 text-gray-600">{li.inventoryItemCode || 'N/A'}</td>
                              <td className="py-1.5 pr-4 text-right text-gray-700">{li.quantity ?? 'N/A'}</td>
                              <td className="py-1.5 pr-4 text-right text-gray-700">{formatCurrency(li.unitAmount)}</td>
                              <td className="py-1.5 pr-4 text-right font-medium text-gray-900">{formatCurrency((li.quantity ?? 0) * (li.unitAmount ?? 0))}</td>
                              <td className="py-1.5 pr-4 text-gray-600">{li.accountCode || 'N/A'}</td>
                              <td className="py-1.5 text-gray-600">{li.taxType || 'N/A'}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t border-gray-300">
                            <td colSpan={5} className="py-1.5 pr-4 text-right text-xs uppercase text-gray-500">Invoice total (ex GST)</td>
                            <td className="py-1.5 pr-4 text-right font-semibold text-gray-900">{formatCurrency(inv.unitAmount)}</td>
                            <td colSpan={2} />
                          </tr>
                        </tfoot>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const RCTIsPreviewTable = ({ rctis }) => {
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (!rctis || rctis.length === 0) {
    return (
      <div className="p-6 text-center text-gray-500 bg-white rounded-lg border border-gray-200">
        No RCTIs found in this batch.
      </div>
    );
  }

  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-x-auto">
      <table className="min-w-full leading-normal">
        <thead>
          <tr className="bg-gray-200 text-left text-gray-600 uppercase text-sm">
            <th className="pl-3 py-3 border-b-2 border-gray-200 w-10"><span className="sr-only">Breakdown</span></th>
            <th className="px-5 py-3 border-b-2 border-gray-200">Status</th>
            <th className="px-5 py-3 border-b-2 border-gray-200">RCTI #</th>
            <th className="px-5 py-3 border-b-2 border-gray-200">Franchisee</th>
            <th className="px-5 py-3 border-b-2 border-gray-200">Due Date</th>
            <th className="px-5 py-3 border-b-2 border-gray-200">Amount (Inc. GST)</th>
            <th className="px-5 py-3 border-b-2 border-gray-200">Ex GST</th>
            <th className="px-5 py-3 border-b-2 border-gray-200">GST</th>
            <th className="px-5 py-3 border-b-2 border-gray-200">Xero Contact</th>
            <th className="px-5 py-3 border-b-2 border-gray-200">Loope Status</th>
          </tr>
        </thead>
        <tbody>
          {rctis.map((rcti) => {
            const { pillClass, label } = getStatusPillProps(rcti.status || 'draft');
            const isOpen = expanded.has(rcti.externalDocNumber);
            const isDerivedDueDate = rcti.dueDateSource === 'derived-rcti-date';
            return (
              <React.Fragment key={rcti.externalDocNumber}>
                <tr className="hover:bg-gray-50">
                  <td className="pl-3 py-4 border-b border-gray-200">
                    <ExpandToggle isOpen={isOpen} onToggle={() => toggle(rcti.externalDocNumber)} docNumber={rcti.externalDocNumber} />
                  </td>
                  <td className="px-5 py-4 border-b border-gray-200 text-sm"><OkPill ok={rcti.ok !== false} /></td>
                  <td className="px-5 py-4 border-b border-gray-200 text-sm font-medium text-gray-900">{rcti.externalDocNumber}</td>
                  <td className="px-5 py-4 border-b border-gray-200 text-sm text-gray-700">
                    <div>{rcti.supplier || rcti.franchiseeName || 'N/A'}</div>
                    <div className="text-xs text-gray-400">{rcti.fseCode}</div>
                  </td>
                  <td className="px-5 py-4 border-b border-gray-200 text-sm text-gray-700">
                    <div>{formatPreviewDate(rcti.dueDate)}</div>
                    {isDerivedDueDate && <div className="text-xs text-amber-700">derived (RCTI date)</div>}
                  </td>
                  <td className="px-5 py-4 border-b border-gray-200 text-sm text-gray-700">{formatCurrency(rcti.unitAmount)}</td>
                  <td className="px-5 py-4 border-b border-gray-200 text-sm text-gray-700">{formatCurrency(rcti.exGst ?? rcti.exGstAmount)}</td>
                  <td className="px-5 py-4 border-b border-gray-200 text-sm text-gray-700">{formatCurrency(rcti.gst ?? rcti.gstAmount)}</td>
                  <td className="px-5 py-4 border-b border-gray-200 text-sm text-gray-700">
                    {rcti.xeroContactId ? (
                      <span className="inline-flex items-center gap-1 text-green-700"><FaCheckCircle /> Resolved</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-yellow-700" title="Will be auto-created on push"><FaExclamationTriangle /> Missing</span>
                    )}
                  </td>
                  <td className="px-5 py-4 border-b border-gray-200 text-sm">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${pillClass}`}>{label}</span>
                      {rcti.ok !== false && <ImportedPill />}
                    </div>
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={10} className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                      <p className="text-xs text-gray-500 mb-3">
                        From <span className="font-medium text-gray-700">{rcti.sourceFile || 'the uploaded RCTI file'}</span>
                        {rcti.sourceRow ? `, CSV row ${rcti.sourceRow}` : ''}
                        {'. The file amount is ex GST; GST is added to reach the amount paid.'}
                      </p>
                      <p className="text-xs text-gray-500 mb-3">
                        Goes to Xero as bill <span className="font-medium text-gray-700">{rcti.externalDocNumber}</span>.
                      </p>
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-xs uppercase text-gray-500 text-left">
                            <th className="py-1 pr-4 font-medium">CSV row</th>
                            <th className="py-1 pr-4 font-medium">Description</th>
                            <th className="py-1 pr-4 font-medium text-right">Qty</th>
                            <th className="py-1 pr-4 font-medium text-right">Unit (inc GST)</th>
                            <th className="py-1 pr-4 font-medium text-right">Line total</th>
                            <th className="py-1 pr-4 font-medium">Account</th>
                            <th className="py-1 font-medium">Tax type</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          <tr>
                            <td className="py-1.5 pr-4 text-gray-500">{rcti.sourceRow ?? 'N/A'}</td>
                            <td className="py-1.5 pr-4 text-gray-700">{rcti.description || 'N/A'}</td>
                            <td className="py-1.5 pr-4 text-right text-gray-700">{rcti.quantity ?? 1}</td>
                            <td className="py-1.5 pr-4 text-right text-gray-700">{formatCurrency(rcti.unitAmount)}</td>
                            <td className="py-1.5 pr-4 text-right font-medium text-gray-900">{formatCurrency((rcti.quantity ?? 1) * (rcti.unitAmount ?? 0))}</td>
                            <td className="py-1.5 pr-4 text-gray-600">{rcti.accountCode || 'N/A'}</td>
                            <td className="py-1.5 text-gray-600">{rcti.taxType || 'N/A'}</td>
                          </tr>
                        </tbody>
                        <tfoot>
                          <tr className="border-t border-gray-300">
                            <td colSpan={4} className="py-1.5 pr-4 text-right text-xs uppercase text-gray-500">RCTI total (inc GST)</td>
                            <td className="py-1.5 pr-4 text-right font-semibold text-gray-900">{formatCurrency(rcti.unitAmount)}</td>
                            <td colSpan={2} />
                          </tr>
                        </tfoot>
                      </table>
                      <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3 text-sm mt-4">
                        <div>
                          <dt className="text-xs uppercase text-gray-500">Raw TotalAmount (ex GST, from the file)</dt>
                          <dd className="text-gray-900 font-medium">
                            {rcti.totalAmountRaw || formatCurrency(rcti.exGst ?? rcti.exGstAmount)}
                            {rcti.gstApplied === false && <span className="text-gray-500">{' '}(GST free — no GST added)</span>}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs uppercase text-gray-500">Tax mapping</dt>
                          <dd className="text-gray-700">
                            TaxCode '{rcti.taxCode || 'N/A'}' maps to {rcti.taxType || 'N/A'}, account {rcti.accountCode || 'N/A'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs uppercase text-gray-500">Amount the franchisee is paid</dt>
                          <dd className="text-gray-700">
                            {formatCurrency(rcti.exGst ?? rcti.exGstAmount)} + {formatCurrency(rcti.gst ?? rcti.gstAmount)} GST
                            {' = '}<span className="font-medium text-gray-900">{formatCurrency(rcti.unitAmount)}</span>
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs uppercase text-gray-500">Due date</dt>
                          <dd className="text-gray-700">
                            {formatPreviewDate(rcti.dueDate)}
                            {isDerivedDueDate ? (
                              <span className="text-amber-700">{' '}(derived: same as RCTI date, no supplier invoice in this upload)</span>
                            ) : (
                              <span className="text-gray-500">{' '}(from the supplier invoice file)</span>
                            )}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs uppercase text-gray-500">Pays to</dt>
                          <dd className="text-gray-700">
                            {rcti.supplierName || rcti.franchiseeName || 'N/A'}
                            {rcti.supplierEmail ? `, ${rcti.supplierEmail}` : ''}
                          </dd>
                        </div>
                      </dl>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// One table per doc type, shown under its own sub-tab (a combined list made invoice and RCTI
// skips for the same FM number read as duplicates).
const SkipsTable = ({ skips, title }) => {
  if (!skips || skips.length === 0) return null;

  return (
    <div className="bg-white rounded-lg border border-red-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-red-50 font-semibold text-red-800">
        <FaTimesCircle className="text-red-500" />
        {title} ({skips.length})
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm divide-y divide-gray-100">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Doc #</th>
              <th className="px-3 py-2 text-left">FSE Code</th>
              <th className="px-3 py-2 text-left">Reason</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {skips.map((skip, idx) => (
              <tr key={`${skip.externalDocNumber}-${skip.type}-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-red-50'}>
                <td className="px-3 py-2 text-gray-700 font-medium">{skip.externalDocNumber || 'N/A'}</td>
                <td className="px-3 py-2 text-gray-600">{skip.fseCode || 'N/A'}</td>
                <td className="px-3 py-2 text-red-600">{skip.reason || 'No reason given'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const BatchUploadTabContent = () => {
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewSubTab, setPreviewSubTab] = useState('invoices');

  // Push state: combined commit + Xero push + email. Retry-safe via idempotent backend.
  const [isPushConfirmOpen, setIsPushConfirmOpen] = useState(false);
  const [isPushing, setIsPushing] = useState(false);
  const [pushError, setPushError] = useState(null);
  const [pushResult, setPushResult] = useState(null); // { successes, failures, commitSkips, mockMode }
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);

  // Dev-only: default on, so an accidental push cannot reach a live Xero org.
  const [mockMode, setMockMode] = useState(true);

  const handleFileInputChange = (e) => {
    setSelectedFiles(Array.from(e.target.files || []));
    setUploadError(null);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setIsUploading(true);
    setUploadError(null);
    try {
      const response = await previewImport(selectedFiles);
      setPreview(response);
      setPreviewSubTab('invoices');
      // A fresh preview invalidates any prior push state.
      setPushResult(null);
      setPushError(null);
    } catch (err) {
      setUploadError(err.message || 'Failed to preview the uploaded files.');
      setPreview(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFiles([]);
    setPreview(null);
    setUploadError(null);
    setPreviewSubTab('invoices');
    setPushResult(null);
    setPushError(null);
    setMockMode(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Combined commit + push to Xero + email RCTIs. Idempotent — safe to retry.
  const handlePushConfirmed = async () => {
    setIsPushing(true);
    setPushError(null);
    try {
      // Only the dev environment can mock; elsewhere the checkbox never renders, so force live.
      const wasMock = IS_DEV_ENV && mockMode;
      const result = await commitAndExecuteImport(preview, wasMock);
      setPushResult({ ...result, mockMode: wasMock });

      // Build results modal data from the combined response.
      const commitSkips = (result.commitSkips || []).map((s) => ({
        invoiceId: s.externalDocNumber,
        status: 'skipped',
        message: s.reason || `Skipped at commit: ${s.type || 'unknown'}`,
      }));
      const successes = (result.successes || []).map((s) => ({
        invoiceId: s.externalDocNumber || s.docId,
        status: 'updated',
        message: s.note ? `${s.action}: ${s.note}` : `${s.action} succeeded`,
      }));
      const failures = (result.failures || []).map((f) => ({
        invoiceId: f.externalDocNumber || f.docId,
        status: 'failed',
        message: f.reason || 'Action failed.',
      }));

      setPushResult((prev) => ({
        ...prev,
        modalData: {
          totalUpdated: successes.length,
          totalFailed: failures.length,
          totalSkipped: commitSkips.length,
          details: [...successes, ...failures, ...commitSkips],
        },
      }));
      setIsResultsModalOpen(true);
    } catch (err) {
      setPushError(err.message || 'Failed to push invoices to Xero.');
    } finally {
      setIsPushing(false);
      setIsPushConfirmOpen(false);
    }
  };

  const invoices = preview?.invoices || [];
  const rctis = preview?.rctis || [];
  const skips = preview?.skips || [];
  const invoiceSkips = skips.filter((s) => s.type === 'invoice');
  const rctiSkips = skips.filter((s) => s.type === 'rcti');
  const summary = preview?.summary || {};
  const okInvoiceCount = invoices.filter((inv) => inv.ok !== false).length;
  const okRctiCount = rctis.filter((rcti) => rcti.ok !== false).length;

  return (
    <div className="space-y-6">
      {/* Upload panel */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 space-y-4">
        <div>
          <h2 className="text-lg font-medium text-gray-800">Upload Batch Files</h2>
          <p className="text-sm text-gray-500 mt-1">
            Upload RCTI and/or Xero-invoice files, CSV or XLSX. Files sharing a filename batch code (e.g.
            RCTI IDEQO0T + Xero Invoice IDEQO0T) are matched into one batch; a file on its own imports
            just its side (RCTI-only due dates default to the RCTI date).
          </p>
        </div>

        <div>
          <label htmlFor="batch-import-files" className="block text-xs font-medium text-gray-600 mb-1">
            Batch files
          </label>
          <input
            id="batch-import-files"
            ref={fileInputRef}
            type="file"
            multiple
            accept=".csv,.xlsx"
            onChange={handleFileInputChange}
            disabled={isUploading}
            className="block w-full text-sm text-gray-600 border border-gray-300 rounded-md shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:border-jims-cyan disabled:opacity-50 disabled:cursor-not-allowed file:mr-4 file:py-2 file:px-4 file:border-0 file:text-sm file:font-semibold file:bg-jims-cyan file:text-white hover:file:bg-jims-cyan-dark"
          />
          {selectedFiles.length > 0 && (
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              {selectedFiles.map((f) => (
                <li key={f.name} className="flex items-center gap-2">
                  <FaFileCsv className="text-gray-400" /> {f.name} ({(f.size / 1024).toFixed(1)} KB)
                </li>
              ))}
            </ul>
          )}
        </div>

        {uploadError && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded flex items-start gap-2">
            <FaTimesCircle className="mt-0.5 flex-shrink-0" />
            <span>{uploadError}</span>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleUpload}
            disabled={selectedFiles.length === 0 || isUploading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-jims-cyan text-white font-semibold rounded-md hover:bg-jims-cyan-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jims-cyan"
          >
            {isUploading ? <FaSpinner className="animate-spin" /> : <FaFileUpload />}
            {isUploading ? 'Uploading...' : 'Upload & Preview'}
          </button>
          {preview && (
            <button
              onClick={handleReset}
              disabled={isUploading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 font-semibold rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
            >
              Start Over
            </button>
          )}
        </div>
      </div>

      {/* Empty state before any preview */}
      {!preview && !isUploading && (
        <div className="p-6 text-center text-gray-500 bg-gray-50 border border-gray-200 rounded-lg flex flex-col items-center gap-2">
          <FaInfoCircle className="text-2xl text-gray-400" />
          Upload a paired batch above to see a preview of what would be imported. Nothing is written or sent until you push to Xero.
        </div>
      )}

      {/* Loading skeleton while the dry-run preview is being built */}
      {isUploading && (
        <div className="space-y-5 animate-pulse">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 h-20" />
            ))}
          </div>
          <div className="bg-white rounded-lg border border-gray-200 h-64" />
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="space-y-5">
          {/* Batches with nothing importable (no batch id, unrecognized file, zero data rows) */}
          {preview.problems?.length > 0 && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded flex items-start gap-2">
              <FaExclamationTriangle className="mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold">
                  {preview.problems.length} batch(es) could not be imported:
                </div>
                <ul className="mt-1 list-disc list-inside text-sm">
                  {preview.problems.map((p) => (
                    <li key={p.batchId || p.reason}>
                      {p.batchId ? `Batch ${p.batchId}` : 'Unidentified batch'}: {p.reason || 'no importable file content'}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Non-blocking notes on batches that WILL import: lone files, empty counterparts, partial matches */}
          {preview.warnings?.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 text-amber-900 rounded flex items-start gap-2">
              <FaInfoCircle className="mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold">This preview imports with {preview.warnings.length} note(s):</div>
                <ul className="mt-1 list-disc list-inside text-sm">
                  {preview.warnings.map((w, i) => (
                    <li key={`${w.batchId || 'general'}-${i}`}>
                      {w.batchId ? `Batch ${w.batchId}: ` : ''}{w.message}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Summary cards */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <SummaryCard icon={FaFileCsv} label="Batch ID" value={preview.batchId || 'N/A'} colorClass="border-gray-200 text-gray-700" />
            <SummaryCard icon={FaCheckCircle} label="Invoices" value={summary.invoicesImported ?? invoices.length} colorClass="border-green-200 text-green-700" />
            <SummaryCard icon={FaCheckCircle} label="RCTIs" value={summary.rctisImported ?? rctis.length} colorClass="border-green-200 text-green-700" />
            <SummaryCard icon={FaTimesCircle} label="Skipped" value={summary.skipped ?? skips.length} colorClass="border-red-200 text-red-700" />
          </div>

          {/* Push panel: single button to commit + push to Xero + email RCTIs */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 space-y-3">
            {pushError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded flex items-start gap-2">
                <FaTimesCircle className="mt-0.5 flex-shrink-0" />
                <span>{pushError}</span>
              </div>
            )}

            {pushResult && (
              <div className="p-3 bg-green-50 border border-green-200 text-green-800 rounded space-y-2">
                <div className="flex items-start gap-2">
                  <FaCheckCircle className="mt-0.5 flex-shrink-0" />
                  <span>
                    Saved {pushResult.createdInvoiceIds?.length ?? 0} invoice(s) and{' '}
                    {pushResult.createdRctiIds?.length ?? 0} RCTI(s).
                    {pushResult.successes?.length ? ` ${pushResult.successes.length} action(s) succeeded.` : ''}
                  </span>
                </div>
                {pushResult.failures?.length > 0 && (
                  <div className="flex items-start gap-2 text-red-700">
                    <FaExclamationTriangle className="mt-0.5 flex-shrink-0" />
                    <span>{pushResult.failures.length} item(s) failed. You can retry — already-completed items will be skipped.</span>
                  </div>
                )}
              </div>
            )}

            {IS_DEV_ENV && (
              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={mockMode}
                  onChange={(e) => setMockMode(e.target.checked)}
                  disabled={isPushing}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-jims-cyan focus:ring-jims-cyan"
                />
                <span>
                  Mock mode — don&apos;t send to Xero
                  <span className="block text-xs text-gray-500">
                    Commits draft records and emails RCTIs to the test inbox; nothing is pushed to Xero.
                  </span>
                </span>
              </label>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setIsPushConfirmOpen(true)}
                disabled={isPushing || (okInvoiceCount === 0 && okRctiCount === 0)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-jims-cyan text-white font-semibold rounded-md hover:bg-jims-cyan-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jims-cyan"
              >
                {isPushing ? <FaSpinner className="animate-spin" /> : <FaCloudUploadAlt />}
                {isPushing
                  ? 'Saving & pushing...'
                  : pushResult?.failures?.length
                  ? 'Retry failed items'
                  : IS_DEV_ENV && mockMode
                  ? 'Commit & Email (no Xero)'
                  : 'Push to Xero & Email'}
              </button>

              {pushResult && pushResult.failures?.length === 0 && (
                <span className="text-xs text-green-600">
                  {pushResult.mockMode
                    ? 'Drafts saved and RCTI emails queued — nothing sent to Xero.'
                    : 'All items sent to Xero and emails queued.'}
                </span>
              )}
              {!pushResult && (
                <span className="text-xs text-gray-400">
                  {IS_DEV_ENV && mockMode
                    ? 'Commits draft records and emails RCTIs to franchisees (test inbox). Nothing is pushed to Xero. Safe to retry.'
                    : 'Commits draft records, pushes invoices/RCTIs to Xero, and emails RCTIs to franchisees. Safe to retry on failure.'}
                </span>
              )}
            </div>
          </div>

          {/* Sub-tab nav */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setPreviewSubTab('invoices')}
                className={`py-2 px-1 border-b-2 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-jims-cyan ${
                  previewSubTab === 'invoices'
                    ? 'border-jims-cyan text-jims-cyan'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Invoices ({invoices.length})
                {invoiceSkips.length > 0 && <span className="ml-1 text-red-600">{invoiceSkips.length} skipped</span>}
              </button>
              <button
                onClick={() => setPreviewSubTab('rctis')}
                className={`py-2 px-1 border-b-2 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-jims-cyan ${
                  previewSubTab === 'rctis'
                    ? 'border-jims-cyan text-jims-cyan'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                RCTIs ({rctis.length})
                {rctiSkips.length > 0 && <span className="ml-1 text-red-600">{rctiSkips.length} skipped</span>}
              </button>
            </nav>
          </div>

          {previewSubTab === 'invoices' && (
            <>
              <InvoicesPreviewTable invoices={invoices} />
              <SkipsTable skips={invoiceSkips} title="Skipped invoice rows" />
            </>
          )}
          {previewSubTab === 'rctis' && (
            <>
              <RCTIsPreviewTable rctis={rctis} />
              <SkipsTable skips={rctiSkips} title="Skipped RCTI rows" />
            </>
          )}
        </div>
      )}

      <ConfirmationModal
        isOpen={isPushConfirmOpen}
        onClose={() => setIsPushConfirmOpen(false)}
        onConfirm={handlePushConfirmed}
        title={pushResult?.failures?.length ? 'Retry failed items' : IS_DEV_ENV && mockMode ? 'Commit & Email (no Xero)' : 'Push to Xero & Email'}
        message={IS_DEV_ENV && mockMode
          ? `This saves ${okInvoiceCount} invoice(s) and ${okRctiCount} RCTI(s) as draft records and emails RCTI PDFs to franchisees. Mock mode is on — nothing is pushed to Xero.${pushResult?.failures?.length ? `\n\n${pushResult.failures.length} item(s) failed previously. Already-completed items will be skipped.` : ''}`
          : `This saves ${okInvoiceCount} invoice(s) and ${okRctiCount} RCTI(s) as draft records, pushes them to Xero, and emails RCTI PDFs to franchisees.${pushResult?.failures?.length ? `\n\n${pushResult.failures.length} item(s) failed previously. Already-completed items will be skipped.` : ''}`}
        isLoading={isPushing}
        loadingText="Saving & pushing..."
        confirmText={pushResult?.failures?.length ? 'Retry' : IS_DEV_ENV && mockMode ? 'Commit & Email' : 'Push to Xero & Email'}
        variant="primary"
      />

      <SyncResultsModal
        isOpen={isResultsModalOpen}
        onClose={() => setIsResultsModalOpen(false)}
        results={pushResult?.modalData}
      />
    </div>
  );
};

export default BatchUploadTabContent;
